import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag, Save, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Input, Button } from '../../Components/ui';
import { AREAS, aplicarLabelsAreas } from '../../utils/areas';
import { useApi } from '../../api/useApi';

// Tela só de master pra renomear as áreas de coordenação/turma (Educacional,
// Esportivo, Cultural, Tecnológico, Capelania) sem precisar mexer em código —
// o VALUE interno de cada uma (usado em `atividades.area`/
// `usuarios.area_coordenacao`) nunca muda, só o nome mostrado em todo o
// sistema (Turmas, Ponto, Grade por Turma, Ajuste Grade, Painel do Gestor,
// Admin Usuários...). Ver backend/areas-config.js.
function AreasConfig() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const api = useApi();
  const [labels, setLabels] = useState(() =>
    Object.fromEntries(AREAS.map((a) => [a.value, a.label])),
  );
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [mensagem, setMensagem] = useState('');

  useEffect(() => {
    if (!user || user.perfil !== 'master') {
      navigate('/');
      return;
    }
    (async () => {
      try {
        const res = await api.get('/api/areas');
        const dados = res?.data ?? res;
        setLabels(dados);
      } catch (error) {
        setErro(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate, api]);

  const salvar = async (e) => {
    e.preventDefault();
    setErro('');
    setMensagem('');
    setSalvando(true);
    try {
      const res = await api.put('/api/areas', labels);
      const dados = res?.data ?? res;
      aplicarLabelsAreas(dados);
      setLabels(dados);
      setMensagem(res?.message || 'Nomes salvos — já valem em todo o sistema.');
    } catch (error) {
      setErro(error.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm shrink-0">
            <Tag className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Áreas</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Nomes de exibição das áreas de turma/coordenação — valem no sistema inteiro.
            </p>
          </div>
        </div>

        {erro && (
          <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 text-red-700 dark:text-red-400 text-sm flex items-start gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {erro}
          </div>
        )}
        {mensagem && (
          <div className="p-4 rounded-2xl bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 text-green-700 dark:text-green-400 text-sm flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            {mensagem}
          </div>
        )}

        <Card>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-3" />
              <span className="text-sm">Carregando...</span>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={salvar}>
              {AREAS.map((a) => (
                <Input
                  key={a.value}
                  label={`${a.label} (código: ${a.value})`}
                  value={labels[a.value] ?? ''}
                  onChange={(e) => setLabels({ ...labels, [a.value]: e.target.value })}
                  maxLength={50}
                  required
                />
              ))}
              <p className="text-xs text-gray-400 dark:text-gray-500">
                O código entre parênteses é só interno (não muda) — é o que fica salvo nas turmas e
                nos coordenadores; só o nome exibido muda.
              </p>
              <div className="flex justify-end pt-2">
                <Button type="submit" variant="primary" icon={Save} disabled={salvando}>
                  {salvando ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          )}
        </Card>
      </main>
    </div>
  );
}

export default AreasConfig;
